import React, { useEffect, useRef, useState } from 'react';
import ReactMapboxGl, { Marker } from 'react-mapbox-gl';
import { ArrowsClockwise, Check, Crosshair, MapPin, Minus, Plus, StackSimple } from '@phosphor-icons/react';
import ENV from 'env';
import { Spot } from 'utils/constants';
import { LatLng, spotLatLng } from 'utils/spotSearch';
import { HoveredPoi, bindPoiHover, ensurePoiLayer, setPoiLayerVisible } from 'utils/pointsOfInterest';
import { DEFAULT_MAP_STYLE, MAP_STYLES, MapStyleOption, mapStylePreviewUrl } from 'utils/mapStyles';
import SpotPopup from './SpotPopup';
import styles from './FindParking.module.css';

const Map = ReactMapboxGl({
    attributionControl: false,
    accessToken: ENV.MAPBOX_TOKEN,
});

/** A view the app asks the map to show. A new object means "move there", even if the values repeat. */
export interface MapView {
    center: LatLng;
    zoom: number;
}

export interface SpotMapProps {
    spots: Spot[];
    view: MapView;
    /** Pixels of the map hidden under the floating results panel; camera moves center in the rest. */
    paddingLeft?: number;
    /** Changes whenever the map's container may have been resized or unhidden; triggers a map resize. */
    layoutKey?: string;
    origin?: LatLng;
    /** Exact point of the last address or venue search, if any. */
    searchMarker?: LatLng | null;
    /** The user's geolocated position, once they have asked for it. */
    userLocation?: LatLng | null;
    rentalType: string;
    activeSpotPk: Spot['pk'] | null;
    loading: boolean;
    showPointsOfInterest: boolean;
    onTogglePointsOfInterest: () => void;
    onHover: (spotPk: Spot['pk'] | null) => void;
    onPinClick: (spotPk: Spot['pk']) => void;
    onMapClick: () => void;
    /** The user pressed "Search Here" after moving the map; search around this center. */
    onSearchHere: (center: LatLng) => void;
    onLocate: (center: LatLng) => void;
}

type TogglePillProps = { checked: boolean; label: string; onToggle: () => void };

const TogglePill: React.FC<TogglePillProps> = ({ checked, label, onToggle }) => (
    <button type="button" role="checkbox" aria-checked={checked} className={styles.togglePill} onClick={onToggle}>
        <span className={`${styles.checkbox} ${checked ? styles.checkboxOn : ''}`} aria-hidden="true">
            <Check size={12} weight="bold" />
        </span>
        {label}
    </button>
);

const isMapUsable = (map: any) => Boolean(map && map.style);

const SpotMap: React.FC<SpotMapProps> = (props) => {
    const {
        spots,
        view,
        paddingLeft = 0,
        layoutKey,
        origin,
        searchMarker,
        userLocation,
        rentalType,
        activeSpotPk,
        loading,
        showPointsOfInterest,
        onTogglePointsOfInterest,
        onHover,
        onPinClick,
        onMapClick,
        onSearchHere,
        onLocate,
    } = props;
    const mapRef = useRef<any>(null);
    // Set when our own zoom buttons move the map, so that move counts as user-initiated.
    const controlMoveRef = useRef(false);
    const [hoveredPoi, setHoveredPoi] = useState<HoveredPoi | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    // True once the user has panned or zoomed away from the last search; shows "Search Here".
    const [mapMoved, setMapMoved] = useState(false);
    const showPoiRef = useRef(showPointsOfInterest);
    showPoiRef.current = showPointsOfInterest;
    const viewRef = useRef(view);
    viewRef.current = view;
    const [mapStyle, setMapStyle] = useState<MapStyleOption>(DEFAULT_MAP_STYLE);
    const [layersOpen, setLayersOpen] = useState(false);
    // Where the style previews are rendered; captured from the live map when the panel opens.
    const [previewView, setPreviewView] = useState<MapView>(view);
    const layersRef = useRef<HTMLDivElement>(null);
    const initialViewApplied = useRef(false);
    const poiHoverBound = useRef(false);
    // The Map component only receives its initial view. Later moves are issued imperatively below,
    // so re-renders never ease the map back to a stale center or zoom while the user is panning.
    const initialCenter = useRef<[number, number]>([view.center.lng, view.center.lat]).current;
    const initialZoom = useRef<[number]>([view.zoom]).current;

    useEffect(() => {
        const map = mapRef.current;
        if (!isMapUsable(map)) return;
        map.easeTo({ center: [view.center.lng, view.center.lat], zoom: view.zoom });
    }, [view]);

    // Mapbox only watches window resizes, so a pane that was display:none needs an explicit resize.
    useEffect(() => {
        const frame = window.requestAnimationFrame(() => {
            const map = mapRef.current;
            if (isMapUsable(map)) map.resize();
        });
        return () => window.cancelAnimationFrame(frame);
    }, [layoutKey]);

    const paneRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const pane = paneRef.current;
        if (!pane) return;
        const observer = new ResizeObserver(() => {
            const map = mapRef.current;
            if (isMapUsable(map)) map.resize();
        });
        observer.observe(pane);
        return () => observer.disconnect();
    }, []);

    const paddingRef = useRef(paddingLeft);
    paddingRef.current = paddingLeft;
    useEffect(() => {
        const map = mapRef.current;
        if (!isMapUsable(map)) return;
        map.setPadding({ top: 0, right: 0, bottom: 0, left: paddingLeft });
    }, [paddingLeft]);

    // A new search (from the search bar, locate, or this button) resets the prompt.
    useEffect(() => {
        setMapMoved(false);
    }, [origin]);

    useEffect(() => {
        if (mapRef.current) setPoiLayerVisible(mapRef.current, showPointsOfInterest);
        if (!showPointsOfInterest) setHoveredPoi(null);
    }, [showPointsOfInterest]);

    useEffect(() => {
        return () => {
            mapRef.current = null;
        };
    }, []);

    // Runs on first load and again after every basemap swap (setStyle drops custom layers).
    const handleStyleLoad = (map: any) => {
        mapRef.current = map;
        if (!initialViewApplied.current) {
            initialViewApplied.current = true;
            const current = viewRef.current;
            map.setPadding({ top: 0, right: 0, bottom: 0, left: paddingRef.current });
            map.jumpTo({ center: [current.center.lng, current.center.lat], zoom: current.zoom });
        }
        ensurePoiLayer(map).then((added) => {
            if (!added) return;
            setPoiLayerVisible(map, showPoiRef.current);
            if (!poiHoverBound.current) {
                poiHoverBound.current = true;
                bindPoiHover(map, setHoveredPoi);
            }
        });
    };

    useEffect(() => {
        if (!layersOpen) return;
        const handleOutsideClick = (event: MouseEvent) => {
            if (layersRef.current && !layersRef.current.contains(event.target as Node)) setLayersOpen(false);
        };
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setLayersOpen(false);
        };
        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [layersOpen]);

    const toggleLayers = () => {
        if (!layersOpen) {
            const map = mapRef.current;
            if (isMapUsable(map)) {
                const live = map.getCenter();
                setPreviewView({ center: { lat: live.lat, lng: live.lng }, zoom: map.getZoom() });
            }
        }
        setLayersOpen((open) => !open);
    };

    const chooseStyle = (style: MapStyleOption) => {
        setMapStyle(style);
        setLayersOpen(false);
    };

    const handleMoveEnd = (map: any, evt: any) => {
        const userInitiated = Boolean(evt && evt.originalEvent) || controlMoveRef.current;
        controlMoveRef.current = false;
        if (!userInitiated) return;
        setMapMoved(true);
    };

    const searchHere = () => {
        const map = mapRef.current;
        if (!isMapUsable(map)) return;
        const next = map.getCenter();
        setMapMoved(false);
        onSearchHere({ lat: next.lat, lng: next.lng });
    };

    const zoomBy = (delta: number) => {
        const map = mapRef.current;
        if (!isMapUsable(map)) return;
        controlMoveRef.current = true;
        map.easeTo({ zoom: map.getZoom() + delta });
    };

    useEffect(() => {
        if (!notice) return;
        const timer = window.setTimeout(() => setNotice(null), 6000);
        return () => window.clearTimeout(timer);
    }, [notice]);

    const locate = () => {
        if (!navigator.geolocation) {
            setNotice('This browser cannot share your location. Search for an address instead.');
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setNotice(null);
                onLocate({ lat: position.coords.latitude, lng: position.coords.longitude });
            },
            (error) => {
                setNotice(
                    error.code === error.PERMISSION_DENIED
                        ? 'Allow location access in your browser to search near you.'
                        : 'Your location could not be found. Try again or search for an address.'
                );
            },
            { timeout: 10000, maximumAge: 60000 }
        );
    };

    // One flat list: the Map component's children type rejects nulls mixed with arrays.
    const markers = spots.map((spot) => {
        const position = spotLatLng(spot);
        const active = spot.pk === activeSpotPk;
        return (
            <Marker
                key={spot.pk}
                coordinates={[position.lng, position.lat]}
                anchor="center"
                style={{ zIndex: active ? 10 : 1 }}
                onMouseEnter={() => onHover(spot.pk)}
                onMouseLeave={() => onHover(null)}
            >
                <div className={styles.pin}>
                    {active && <SpotPopup spot={spot} rentalType={rentalType} origin={origin} />}
                    <button
                        type="button"
                        className={`${styles.pinDot} ${active ? styles.pinDotActive : ''}`}
                        aria-label={`${spot.name} on map`}
                        aria-expanded={active}
                        onClick={() => onPinClick(spot.pk)}
                    />
                </div>
            </Marker>
        );
    });
    if (searchMarker) {
        markers.push(
            <Marker
                key="search-result"
                coordinates={[searchMarker.lng, searchMarker.lat]}
                anchor="bottom"
                style={{ zIndex: 5, pointerEvents: 'none' }}
            >
                <div className={styles.searchPin} role="img" aria-label="Search result location">
                    <MapPin size={36} weight="fill" />
                    <span className={styles.searchPinLabel}>Search Result</span>
                </div>
            </Marker>
        );
    }
    if (userLocation) {
        markers.push(
            <Marker
                key="user-location"
                coordinates={[userLocation.lng, userLocation.lat]}
                anchor="center"
                style={{ zIndex: 4, pointerEvents: 'none' }}
            >
                <div className={styles.userPin} role="img" aria-label="Your location" />
            </Marker>
        );
    }
    if (hoveredPoi) {
        markers.push(
            <Marker
                key="poi-tooltip"
                coordinates={[hoveredPoi.lng, hoveredPoi.lat]}
                anchor="bottom"
                offset={[0, -18]}
                style={{ zIndex: 20, pointerEvents: 'none' }}
            >
                <div className={styles.poiTooltip} role="tooltip">
                    <span className={styles.poiTooltipName}>{hoveredPoi.name}</span>
                    <span className={styles.poiTooltipCategory}>{hoveredPoi.category}</span>
                </div>
            </Marker>
        );
    }

    return (
        <div ref={paneRef} className={styles.mapPane}>
            <Map
                style={mapStyle.url}
                center={initialCenter}
                zoom={initialZoom}
                containerStyle={{ height: '100%', width: '100%' }}
                onStyleLoad={handleStyleLoad}
                onMoveEnd={handleMoveEnd}
                onClick={() => onMapClick()}
            >
                {markers}
            </Map>

            <div className={styles.mapTopLeft}>
                <TogglePill
                    checked={showPointsOfInterest}
                    label="Points of Interest"
                    onToggle={onTogglePointsOfInterest}
                />
            </div>

            <button
                type="button"
                className={`${styles.searchHere} ${mapMoved && !loading ? styles.searchHereVisible : ''}`}
                aria-hidden={!mapMoved || loading}
                tabIndex={mapMoved && !loading ? 0 : -1}
                onClick={searchHere}
            >
                <ArrowsClockwise size={15} weight="bold" className={styles.searchHereIcon} />
                Search Here
            </button>

            <div className={`${styles.mapControl} ${styles.zoomStack}`}>
                <button type="button" className={styles.mapButton} aria-label="Zoom in" onClick={() => zoomBy(1)}>
                    <Plus size={16} />
                </button>
                <div className={styles.zoomDivider} />
                <button type="button" className={styles.mapButton} aria-label="Zoom out" onClick={() => zoomBy(-1)}>
                    <Minus size={16} />
                </button>
            </div>

            <div className={styles.layers} ref={layersRef}>
                {layersOpen && (
                    <div className={styles.layersPanel} role="radiogroup" aria-label="Map style">
                        {MAP_STYLES.map((style) => {
                            const active = style.id === mapStyle.id;
                            return (
                                <button
                                    key={style.id}
                                    type="button"
                                    role="radio"
                                    aria-checked={active}
                                    className={`${styles.layerOption} ${active ? styles.layerOptionActive : ''}`}
                                    onClick={() => chooseStyle(style)}
                                >
                                    <img
                                        className={styles.layerPreview}
                                        src={mapStylePreviewUrl(style, previewView.center, previewView.zoom)}
                                        alt=""
                                    />
                                    <span className={styles.layerLabel}>{style.label}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
                <div className={`${styles.mapControl} ${styles.layersButton}`}>
                    <button
                        type="button"
                        className={styles.mapButton}
                        aria-label="Change map style"
                        aria-expanded={layersOpen}
                        onClick={toggleLayers}
                    >
                        <StackSimple size={18} />
                    </button>
                </div>
            </div>

            <div className={`${styles.mapControl} ${styles.locateButton}`}>
                <button type="button" className={styles.mapButton} aria-label="Use my location" onClick={locate}>
                    <Crosshair size={18} />
                </button>
            </div>

            {loading && (
                <div className={`${styles.mapControl} ${styles.loadingPill}`} role="status">
                    Updating results
                </div>
            )}
            {notice && !loading && (
                <div className={`${styles.mapControl} ${styles.loadingPill} ${styles.noticePill}`} role="alert">
                    {notice}
                </div>
            )}
        </div>
    );
};

export default SpotMap;
